/**
 * Structured product information, rendered from the canonical fact sheet.
 *
 * Every value comes from `src/lib/product-facts.ts` (built from the merchant's
 * own product content). Sections and rows without verified data are simply not
 * rendered — nothing is defaulted or inferred.
 */
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  careRows,
  deliveryRows,
  factsCompleteness,
  gpsrRows,
  originLine,
  safetyRows,
  specRows,
  type ProductFacts,
  type SpecRow,
} from "@/lib/product-facts";

function Rows({ rows }: { rows: SpecRow[] }) {
  return (
    <dl className="divide-y divide-border/60">
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-3 gap-4 py-2.5">
          <dt className="col-span-1 text-xs uppercase tracking-wider text-muted-foreground">{r.label}</dt>
          <dd className="col-span-2 whitespace-pre-line text-sm text-foreground">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Section({
  title,
  defaultOpen = false,
  children,
}: {
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border/70 last:border-b-0">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-4 py-4 text-left"
        >
          <span className="text-sm font-medium tracking-wide text-foreground">{title}</span>
          <ChevronDown
            aria-hidden
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
      </h3>
      {open && <div className="pb-5">{children}</div>}
    </div>
  );
}

export function ProductSpecSheet({
  facts,
  extras,
}: {
  facts: ProductFacts;
  /** Merchant-filled Shopify metafields, appended to Tuotetiedot. */
  extras?: SpecRow[];
}) {
  const specs = [...specRows(facts), ...(extras ?? [])];
  const safety = safetyRows(facts);
  const care = careRows(facts);
  const delivery = deliveryRows(facts);
  const gpsr = gpsrRows(facts);
  const quality = import.meta.env.DEV ? factsCompleteness(facts) : null;

  // Nothing verified for this SKU → render no spec sheet at all rather than
  // an empty shell of accordions.
  if (specs.length === 0 && safety.length === 0 && care.length === 0) return null;

  return (
    <section className="mt-8 rounded-2xl border border-border bg-cream/60 px-5 md:px-6">
      {specs.length > 0 && (
        <Section title="Tuotetiedot" defaultOpen>
          <Rows rows={specs} />
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">{originLine(facts)}</p>
        </Section>
      )}

      {safety.length > 0 && (
        <Section title="Turvallisuus">
          <Rows rows={safety} />
          <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
            Näytämme vain tiedot, jotka valmistaja on vahvistanut tälle tuotteelle. Tarkista aina
            kokoamisohje ja valvo pientä lasta leikin aikana.
          </p>
        </Section>
      )}

      <Section title="Toimitus ja palautus">
        <Rows rows={delivery} />
      </Section>

      {/* GPSR (EU 2023/988): manufacturer + EU-responsible operator must be
          identifiable on every product page. */}
      <Section title="Valmistaja ja tuoteturvallisuus">
        <Rows rows={gpsr} />
        <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
          Tuoteturvallisuutta koskevissa kysymyksissä ja vikailmoituksissa ota yhteyttä:{" "}
          <a className="underline" href="mailto:asiakaspalvelu@montessori24.fi">
            asiakaspalvelu@montessori24.fi
          </a>
          . Jos valmistajan yhteystietoja ei ole tässä listattu, toimitamme ne pyynnöstä.
        </p>
      </Section>

      {care.length > 0 && (
        <Section title="Hoito-ohjeet">
          <Rows rows={care} />
        </Section>
      )}

      {quality && quality.missing.length > 0 && (
        <div className="my-4 rounded-lg border border-dashed border-amber-500/60 bg-amber-500/5 p-3 text-xs text-amber-900">
          <strong>Dev-only tuotetiedon laatu: {quality.percent} %.</strong> Puuttuu:{" "}
          {quality.missing.join(", ")}.
        </div>
      )}
    </section>
  );
}

export default ProductSpecSheet;
